<template>
    <div v-clickaway="closeSlotsTooltip" :class="dayClass" @click="toggleSlotsTooltip($event)">
        <div class="avail-day__header">
            <span :class="dayNumberClasses">{{ date.format('D') }}</span>
        </div>

        <div class="avail-day__badges">
            <span v-if="hasAvailableSlots" class="avail-day__badge avail-day__badge--available">
                {{ $t('pages.availability.index.calendar.available') }}
            </span>
            <span v-if="showNotAvailable" class="avail-day__badge avail-day__badge--not-available">
                {{ $t('pages.availability.index.calendar.not_available') }}
            </span>
        </div>

        <div v-if="hasAvailableSlots" ref="tooltip">
            <external-calendar-slots
                ref="externalCalendarSlots"
                :year="year"
                :month="month"
                :day="day"
                :physician="physician"
                :isOpen="isSlotsTooltipOpen"
                :selectedSlotsByDate="selectedSlotsByDate"
                @emitEvent="emitEvent($event)"
            ></external-calendar-slots>
        </div>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import CalendarDayMixin from 'mixins/CalendarDayMixin'
    import ExternalCalendarSlots from 'components/page-elements/referrals-new-schedule/ExternalCalendarSlots'

    export default {
        mixins: [CalendarDayMixin],

        components: {
            ExternalCalendarSlots,
        },

        props: {
            selectedSlotsByDate: { type: Object, required: false, default: undefined },
        },

        computed: {
            isToday() {
                return dayjs().isSame(this.date, 'date')
            },

            hasAvailableSlots() {
                return this.slotsCount > 0 && this.date.isSameOrAfter(dayjs(), 'date')
            },

            showNotAvailable() {
                return !this.hasAvailableSlots
            },

            dayClass() {
                return {
                    'avail-day': true,
                    'avail-day--not-available': this.showNotAvailable,
                    'avail-day--open': this.isSlotsTooltipOpen,
                }
            },

            dayNumberClasses() {
                return {
                    'avail-day__num': true,
                    'avail-day__num--today': this.isToday,
                }
            },
        },
    }
</script>

<style scoped>
    .avail-day {
        position: relative;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 12px;
        min-height: 130px;
        width: 100%;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        cursor: pointer;
        transition: box-shadow 0.15s ease;
    }

    .avail-day:hover,
    .avail-day--open {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .avail-day--not-available {
        border-color: #fecaca;
        cursor: default;
    }

    .avail-day__header {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .avail-day__num {
        font-size: 14px;
        font-weight: 500;
        color: #1f2937;
        line-height: 1;
    }

    .avail-day__num--today {
        color: #0891b2;
        font-weight: 700;
    }

    .avail-day__badges {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        padding-top: 8px;
    }

    .avail-day__badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        max-width: 96px;
        min-height: 20px;
        font-size: 12px;
        font-weight: 400;
        line-height: 1.2;
        padding: 2px 10px;
        border-radius: 4px;
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        box-sizing: border-box;
    }

    .avail-day__badge--available {
        background: #e5e7eb;
        color: #4b5563;
    }

    .avail-day__badge--not-available {
        background: #fee2e2;
        color: #dc2626;
    }

    @media (max-width: 1024px) {
        .avail-day {
            padding: 10px;
            min-height: 110px;
        }

        .avail-day__badge {
            font-size: 11px;
            min-height: 18px;
            padding: 2px 8px;
        }
    }

    @media (max-width: 768px) {
        .avail-day {
            padding: 8px;
            min-height: 90px;
            border-radius: 6px;
        }

        .avail-day__num {
            font-size: 12px;
        }

        .avail-day__badge {
            font-size: 9px;
            min-height: 18px;
            padding: 1px 8px;
        }

        .avail-day__badges {
            gap: 3px;
        }
    }
</style>
